import classNames from 'classnames';
import { ReactNode } from 'react';
import ReactTooltip, { TooltipProps as _TooltipProps } from 'react-tooltip';
import './tooltip.scss';

const defaultSanitize: (str: string, ...args: any[]) => string = (
  str: string
) => {
  // eslint-disable-next-line no-console
  console.warn(
    `Tooltip component do not have a sanitizer.
  - Set default sanitizer: Tooltip.setDefaultSanitizer(sanitizeMethod)
  - Or pass a sanitizer as prop: <Tooltip sanitize={sanitizeMethod} />`
  );
  return str;
};

const fixDocument = () => {
  // Elevio is overriding the document constructor and react-tooltip relies on it
  // cf. https://github.com/wwayne/react-tooltip/blob/v4.2.14/src/index.js#L192
  try {
    const value = 'HTMLDocument';
    if (document.constructor.name !== value) {
      Object.defineProperty(document.constructor, 'name', { value });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
  }
};

export interface TooltipProps extends _TooltipProps {
  children?: ReactNode;
  className?: string;
  delayHide?: number;
  delayShow?: number;
  disabled?: boolean;
  hoverable?: boolean;
  id: string;
  place?: ReactTooltip['props']['place'];
  hoverableDelayHide?: number;
  html?: boolean;
  sanitize?: typeof defaultSanitize;
  sanitizeOptions?: { [key: string]: any };
}

export function Tooltip({
  id,
  className,
  disabled,
  delayShow,
  delayHide,
  place,
  children,
  hoverable,
  hoverableDelayHide = 250,
  html,
  sanitize,
  sanitizeOptions,
  ...otherProps
}: TooltipProps) {
  if (disabled) {
    return null;
  }

  let _delayHide = delayHide;
  if (!_delayHide && hoverable) {
    _delayHide = hoverableDelayHide;
  }

  const localSanitize =
    typeof sanitize === 'function' ? sanitize : defaultSanitize;

  fixDocument();

  return (
    <ReactTooltip
      id={id}
      className={classNames(className, 'Tooltip', {
        'Tooltip--hoverable': hoverable,
      })}
      place={place}
      effect="solid"
      delayShow={delayShow}
      delayHide={_delayHide}
      html={typeof children === 'string' || html}
      getContent={(val: any) =>
        html && typeof val === 'string'
          ? localSanitize(val, sanitizeOptions)
          : val
      }
      {...otherProps}
    >
      {typeof children === 'string'
        ? localSanitize(children, sanitizeOptions)
        : children}
    </ReactTooltip>
  );
}

Tooltip.defaultProps = {
  place: 'top',
  delayShow: 500,
  disabled: false,
  hoverable: false,
  sanitize: defaultSanitize,
  sanitizeOptions: { ADD_ATTR: ['target'] },
};

Tooltip.show = ReactTooltip.show;

Tooltip.setDefaultSanitizer = (sanitize: typeof defaultSanitize) => {
  Tooltip.defaultProps.sanitize = sanitize;
};
